import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { userSessionsColumns } from "./tableParams/userSessionsColumns";
import StatsAPI from "../../../api/StatsAPI";
import DataTable from "../../dataTable/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";

import type { JSX } from "react";

import type { UserSession } from "../../../api/StatsAPI.types";

interface UserSessionsProps {
  userId: string;
}

function getSessionId(session: UserSession): string {
  return session.session_uuid;
}

function UserSessions({ userId }: UserSessionsProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const getUserSessionsQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getUserSessions,
    params: [{ userId, projectSlug }],
  });

  const {
    data: userSessions,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...getUserSessionsQuery,
    enabled: !!userId,
  });

  const columnList = useMemo(() => userSessionsColumns({ t }), [t]);

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("usersSessions")}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={userSessions?.data ?? []}
          getRowId={getSessionId}
          isError={isError}
          isLoading={isLoading}
          onRetry={refetch}
          urlSync={{ prefix: "sessions" }}
        />
      </CardContent>
    </Card>
  );
}

export default UserSessions;
